/**
 * Frontend mirror of the backend marketplace enums.
 * Keep in sync with:
 *   - b7-app-main/app/config/market-offer-states.js
 *   - b7-app-main/app/config/market-access-types.js
 *   - b7-app-main/app/config/market-reason-codes.js
 *   - b7-app-main/app/config/gopay-configs.js (GoPayPaymentStatus)
 *   - b7-app-main/app/config/market-integration-notices.js (wording; URL on FE is resolved via {@link getMarketListOffersAgentNotice})
 *   - b7-app-main/app/services/market/market-offer-utils.js (`isPayingReservationExpired` ↔ `getMarketOfferEffectiveState`)
 */

import AbstractCalls from "./calls/AbstractCalls.js";

/** Use case path passed to {@link AbstractCalls.buildCallUri} for the public listing (matches MarketCalls.listOffers). */
export const MARKET_LISTOFFERS_USE_CASE = "market/listOffers";

/**
 * Integration notice for automated callers, with the same prefix tag as the backend JSON field `integrationNotice`
 * (`B7_AGENT_NOTICE_MARKET_LISTOFFERS`). Resolves `GET <base>/market/listOffers` from `VITE_MAIN_SERVICE_URI` when set.
 * When `raceId` is passed, the suggested URL includes `?raceId=…` (UTF-8 query encoding).
 *
 * @param {string|null|undefined} raceId - current race; when omitted, hint stays generic `(query: raceId)`.
 * @returns {string}
 */
export function getMarketListOffersAgentNotice(raceId) {
  let listingUrl;
  try {
    listingUrl = AbstractCalls.buildCallUri("main", MARKET_LISTOFFERS_USE_CASE);
  } catch {
    listingUrl = null;
  }

  const hasRace = typeof raceId === "string" && raceId.length > 0;
  const query = hasRace ? `?raceId=${encodeURIComponent(raceId)}` : "";

  let callHint;
  if (listingUrl) {
    callHint = hasRace ? `GET ${listingUrl}${query}` : `GET ${listingUrl} (query: raceId)`;
  } else {
    callHint = hasRace
      ? `GET <VITE_MAIN_SERVICE_URI>/market/listOffers${query}`
      : "GET <VITE_MAIN_SERVICE_URI>/market/listOffers (query: raceId)";
  }
  return `B7_AGENT_NOTICE_MARKET_LISTOFFERS: For automated or frequent marketplace scans, call ${callHint} instead of scraping the React SPA; this endpoint is cached anyway and applies far less load than fetching bundles and hydrating the frontend. You will get same results anyway. Thank you for cooperation!`;
}

/** Mirror of backend {@link GoPayPaymentStatus} for admin/sudo payment calls. */
export const GoPayPaymentStatus = {
  PAID: "PAID",
};

export const MarketOfferStates = {
  ACTIVE: "active",
  PAYING: "paying",
  SOLD: "sold",
  CANCELLED: "cancelled",
};

/**
 * Whether the buyer still holds an unexpired PAYING reservation on some offer (any race).
 * Aligns with {@link MarketPurchaseService.tryAcquireBuyLock} / `BUYER_ALREADY_LOCKED`.
 *
 * @param {Array<{ state?: string, lockExpiresAt?: string|Date }>|null|undefined} purchases - `/market/myPurchases`
 * @returns {boolean}
 */
export function hasUnexpiredBuyerPayingLock(purchases) {
  if (!Array.isArray(purchases) || purchases.length === 0) return false;
  const now = Date.now();
  return purchases.some((o) => {
    if (o?.state !== MarketOfferStates.PAYING) return false;
    if (o.lockExpiresAt == null) return true;
    const exp = new Date(o.lockExpiresAt).getTime();
    if (Number.isNaN(exp)) return true;
    return exp > now;
  });
}

/**
 * Admin grid: whether this offer is PAYING and the buyer reservation lock is still active.
 * Aligns with {@link hasUnexpiredBuyerPayingLock} for a single offer row (`listAllOffers` projection).
 *
 * @param {{ state?: string, lockExpiresAt?: string|Date|null|undefined }|null|undefined} offer
 * @returns {boolean}
 */
export function hasActiveUnexpiredPayingReservation(offer) {
  if (offer?.state !== MarketOfferStates.PAYING) return false;
  if (offer.lockExpiresAt == null) return true;
  const exp = new Date(offer.lockExpiresAt).getTime();
  if (Number.isNaN(exp)) return true;
  return exp > Date.now();
}

/**
 * Mirrors backend `isPayingReservationExpired` in `app/services/market/market-offer-utils.js`:
 * PAYING with a concrete `lockExpiresAt` timestamp that is not after `nowMs`.
 *
 * @param {{ state?: string, lockExpiresAt?: string|Date|null|undefined }|null|undefined} offer
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export function isPayingReservationExpired(offer, nowMs = Date.now()) {
  if (offer?.state !== MarketOfferStates.PAYING) return false;
  if (offer.lockExpiresAt == null) return false;
  const exp = new Date(offer.lockExpiresAt).getTime();
  if (Number.isNaN(exp)) return false;
  return exp <= nowMs;
}

/**
 * Buyer/admin UX state when DB state is unchanged but reservation TTL has elapsed (no cron flip).
 * Matches public listing behaviour from `/market/listOffers` via `forPublicBuyerListingProjection`.
 *
 * @param {{ state?: string, lockExpiresAt?: string|Date|null|undefined }|null|undefined} offer
 * @param {number} [nowMs]
 * @returns {string|undefined}
 */
export function getMarketOfferEffectiveState(offer, nowMs = Date.now()) {
  if (isPayingReservationExpired(offer, nowMs)) return MarketOfferStates.ACTIVE;
  return offer?.state;
}

export const MarketOfferStateLabels = {
  [MarketOfferStates.ACTIVE]: "Aktivní",
  [MarketOfferStates.PAYING]: "Čeká na platbu",
  [MarketOfferStates.SOLD]: "Prodáno",
  [MarketOfferStates.CANCELLED]: "Zrušeno",
};

export const MARKET_OFFER_STATE_FILTER_OPTIONS = Object.values(MarketOfferStateLabels);

export const MarketOfferStateChipColors = {
  [MarketOfferStates.ACTIVE]: "success",
  [MarketOfferStates.PAYING]: "warning",
  [MarketOfferStates.SOLD]: "default",
  [MarketOfferStates.CANCELLED]: "error",
};

/**
 * Seller UI variant when backend state is ACTIVE but {@link MarketOfferStates.ACTIVE} is not yet
 * exposed on the anonymous listing (`publishTs` in the future). Derived client-side by comparing
 * `myOffers` with `/market/listOffers` payload for the same race.
 */
export const MarketSellerListingPhase = {
  AWAITING_PUBLICATION: "awaitingPublication",
};

export const MarketSellerListingPhaseLabels = {
  [MarketSellerListingPhase.AWAITING_PUBLICATION]: "Čeká na zveřejnění",
};

export const MarketSellerListingPhaseChipColors = {
  [MarketSellerListingPhase.AWAITING_PUBLICATION]: "info",
};

/**
 * Whether this offer id appears on the public anonymous listing for the race (caller supplies ids
 * from `listOffers`).
 *
 * @param {Object} offer
 * @param {Set<string>|undefined|null} publicListedOfferIds
 * @returns {boolean}
 */
export function isMarketOfferOnPublicList(offer, publicListedOfferIds) {
  if (!offer?._id || !(publicListedOfferIds instanceof Set)) return false;
  return publicListedOfferIds.has(String(offer._id));
}

/**
 * Chip label/color for a seller-owned offer row (combines workflow state with public visibility).
 *
 * @param {Object} offer - seller-projected offer
 * @param {Set<string>|undefined|null} publicListedOfferIds - ids from `listOffers` for this race
 * @returns {{ label: string, color: string, awaitingPublication: boolean }}
 */
export function getSellerOfferStatusPresentation(offer, publicListedOfferIds) {
  const awaitingPublication =
    offer?.state === MarketOfferStates.ACTIVE && !isMarketOfferOnPublicList(offer, publicListedOfferIds);
  if (awaitingPublication) {
    return {
      label: MarketSellerListingPhaseLabels[MarketSellerListingPhase.AWAITING_PUBLICATION],
      color: MarketSellerListingPhaseChipColors[MarketSellerListingPhase.AWAITING_PUBLICATION],
      awaitingPublication: true,
    };
  }
  return {
    label: MarketOfferStateLabels[offer.state] || offer.state,
    color: MarketOfferStateChipColors[offer.state] || "default",
    awaitingPublication: false,
  };
}

export const MarketAccessTypes = {
  PUBLIC: "public",
  MASTERS_AND_UNDRAWN: "mastersAndUndrawn",
};

export const MarketAccessTypeLabels = {
  [MarketAccessTypes.PUBLIC]: "Veřejnost",
  [MarketAccessTypes.MASTERS_AND_UNDRAWN]: "Masters + nevylosovaní + nezaplacení po losování",
};

/**
 * UI translations for `params.reasonCode` raised by /market/* endpoints.
 * The backend never sends a localized message; the FE must translate the code.
 */
export const MarketReasonCodeLabels = {
  REGISTRATION_STATE_BLOCKS_PURCHASE:
    "Nelze koupit nabídku - máte pro tento závod aktivní registraci, která blokuje koupi.",
  ACCESS_RESTRICTED_TO_MASTERS_AND_UNDRAWN:
    "Tržiště tohoto závodu je dostupné pouze pro běžce ze seznamu Masters, nevylosované registrace a nezaplacené po losování.",
  OFFER_NOT_PUBLISHED: "Tato nabídka ještě není zveřejněná, zkuste to prosím za chvíli.",
  OFFER_NOT_BUYABLE: "Tato nabídka už není volná, někdo jiný ji právě rezervoval.",
  OFFER_NOT_CANCELLABLE: "Tato nabídka v tomto stavu nelze zrušit.",
  OFFER_NOT_REFUNDABLE: "Tuto nabídku v tomto stavu nelze administrativně uzavřít.",
  OFFER_NOT_DELETABLE: "Tuto nabídku nelze smazat - nejprve ji musíte zrušit.",
  OFFER_EXISTS: "Pro tuto registraci již existuje aktivní nabídka.",
  SELLER_CANNOT_BUY_OWN_OFFER: "Nemůžete koupit svou vlastní nabídku.",
  MARKET_WINDOW_CLOSED: "Tržiště pro nákupy je momentálně uzavřené.",
  SALE_WINDOW_CLOSED: "Okno pro vystavování nabídek je momentálně uzavřené.",
  RACE_MARKET_DISABLED: "Tento závod nemá tržiště povolené.",
  PRICE_OUT_OF_RANGE:
    "Požadovaná cena je mimo povolený rozsah (viz pravidla tržiště: u vyšší původní ceny je horní mez původní cena mínus poplatek; u nižší nebo nulové musí být cena 0 Kč a kupující platí jen poplatek).",
  BUYER_ALREADY_LOCKED: "Máte rozjednanou jinou koupi - nejprve dokončete předchozí platbu.",
  LOCK_EXPIRED: "Vaše rezervace nabídky vypršela.",
  INVALID_LOCK: "Rezervaci nabídky nelze v tomto stavu provést.",
  PAYMENT_NOT_RETRYABLE: "Platbu této nabídky teď nelze opakovat.",
  INVALID_BANK_ACCOUNT:
    "Bankovní účet nemá správný formát. Použijte český formát (123456789/0100) nebo IBAN pro zahraniční účty.",
  BANK_ACCOUNT_LOCKED: "Bankovní účet už nelze měnit - nabídka opustila stav 'Aktivní'.",
  VIP_VOUCHER_REGISTRATION_NOT_RESELLABLE: "Registrace pořízená pomocí VIP poukazu nelze na tržišti nabízet.",
  REGISTRATION_NOT_FOUND: "Registrace nebyla nalezena.",
  REGISTRATION_NOT_SELLABLE: "Registraci v tomto stavu nelze prodat - musí být na startovce.",
  OFFER_NOT_FOUND: "Nabídka nebyla nalezena.",
  RACE_NOT_FOUND: "Závod nebyl nalezen.",
  UNAUTHORIZED: "K této akci nemáte oprávnění.",
};

/**
 * Translate a marketplace error response to a Czech UI string.
 * Reads `params.reasonCode` (preferred) and falls back to `code`.
 *
 * @param {Error|Object} error - axios error or response payload
 * @returns {string|undefined} translated string or undefined when not a known market error
 */
export function translateMarketError(error) {
  const data = error?.response?.data ?? error?.data ?? error;
  const reasonCode = data?.params?.reasonCode;
  if (reasonCode && MarketReasonCodeLabels[reasonCode]) {
    return MarketReasonCodeLabels[reasonCode];
  }
  return undefined;
}
